import { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/** Registers the service worker and, when a new version is waiting, shows a sticky
 *  toast with an "Обнови" button.
 *
 *  This is the whole reason `registerType` is `prompt` (not `autoUpdate`): an
 *  installed PWA on a phone otherwise only picks up a new build after a full
 *  close/reopen — or a reinstall. Tapping the button calls `updateServiceWorker(true)`,
 *  which posts SKIP_WAITING to the waiting worker (handled in sw.ts); the plugin's
 *  registration then reloads the page once the new worker takes control. A single,
 *  user-initiated update — no autoUpdate reload loop.
 *
 *  Renders nothing itself; the toast is the whole UI. Mounted once, alongside the
 *  sonner <Toaster>. */
export function PwaUpdatePrompt() {
  const { t } = useTranslation();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  useEffect(() => {
    if (!needRefresh) return;
    const id = toast(t('pwa.updateTitle'), {
      description: t('pwa.updateBody'),
      // Stays until acted on — a version prompt the user can dismiss and forget
      // defeats the point; the next visit would apply it anyway, but the whole
      // value here is updating on the spot.
      duration: Infinity,
      action: {
        label: t('pwa.updateAction'),
        onClick: () => updateServiceWorker(true),
      },
    });
    return () => {
      toast.dismiss(id);
    };
  }, [needRefresh, t, updateServiceWorker]);

  return null;
}
